import {useContext, useEffect, useMemo, useRef, useState, useLayoutEffect} from "react";
import {useQuery} from "@apollo/client";
import {AppContext, AppContextI} from "../context/ContextWrapper";
import {
    Box,
    Container,
    ExpandedIndex,
    Grid,
    HStack,
    IconButton,
    Input,
    Text,
    Tooltip
} from "@chakra-ui/react";
import {
    AreaLanguageOption,
    BeaconTrigger,
    CircleSpot,
    GpsTrigger,
    MarkerSpot,
    PolygonSpot,
    Spot,
    SpotInfo,
    SpotType,
    TextTranslation,
    TriggerRule,
    TriggerRules,
    WiFiApTrigger
} from "../types";
import {BsSoundwave} from "react-icons/bs";
import {BiCopy, BiTrash} from "react-icons/bi";
import {FaBluetoothB, FaWifi} from "react-icons/fa";
import {FiFlag} from "react-icons/fi";
import {MapContext, MapContextI} from "../context/MapContextWrapper";
import {useConfirmDialog} from "../common/ConfirmDialogProvider";
import {findReferencesToSpot} from "../rules/ruleReferences";
import {buildDeleteSpotConfirmBody} from "../rules/deleteConfirmBody";
import {v4 as uuidv4} from "uuid";
import {useTranslationDialog} from "../translations/TranslationDialogProvider";
import SpotDetailsAccordion from "./SpotDetailsAccordion";
import {getTextTranslationsQuery} from "../translations/textTranslationQueries";
import {
    buildSpotTranslationRowsByClientId,
    getSpotTranslationState as computeSpotTranslationState,
    LocalizedContentState,
    normalizeLanguageCode,
    SPOT_TRANSLATION_FIELDS,
} from "../translations/spotTranslationState";
const ICON_SLOT_SIZE_PX = 24;

const SpotList = () => {
	    const appContext: AppContextI = useContext<AppContextI>(AppContext);
	    const mapContext: MapContextI = useContext<MapContextI>(MapContext);
        const {confirm} = useConfirmDialog();
        const {open: openTranslationDialog} = useTranslationDialog();
    const area = appContext.getArea();
    const containerRef = useRef<HTMLDivElement | null>(null);
	const listScrollRef = useRef<HTMLDivElement | null>(null);
    const [bottomHeightPx, setBottomHeightPx] = useState<number>(535);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [originalUserSelect, setOriginalUserSelect] = useState<string | undefined>(undefined);
	    const [nameInput, setNameInput] = useState<string>("");
    const [spotTranslations, setSpotTranslations] = useState<TextTranslation[]>([]);
    const [spotAccordionSections, setSpotAccordionSections] = useState<ExpandedIndex>([0]);
	    const beaconRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
    const beaconRegexNoDelimiter = /^([0-9A-Fa-f]{12})$/;
    const isValidBeaconAddress = (addr?: string) => !!addr && (beaconRegex.test(addr) || beaconRegexNoDelimiter.test(addr));

    const dividerHeightPx = 6;
	const minBottomPx = 200;
	const minTopPx = 50;

    const spots = (appContext.getSpots() || []).filter((spot): spot is Spot => !!spot && spot.id !== undefined && spot.key !== undefined);
	const sortedSpots = useMemo(
		() => [...spots].sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, {numeric: true, sensitivity: "base"})),
		[spots]
	);
	const listItemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const areaLanguages = useMemo(() => area?.languages?.available || [], [area?.languages?.available]);
    const areaLanguageCodes = useMemo(
        () => areaLanguages
            .map((language: AreaLanguageOption) => normalizeLanguageCode(language.code))
            .filter((languageCode, index, values) => !!languageCode && values.indexOf(languageCode) === index),
        [areaLanguages]
    );
    const normalizedDefaultLanguage = normalizeLanguageCode(area?.languages?.default);
    const translatableLanguageCodes = useMemo(
        () => areaLanguageCodes.filter((languageCode) => languageCode !== normalizedDefaultLanguage),
        [areaLanguageCodes, normalizedDefaultLanguage]
    );
    const spotClientIds = useMemo(
        () => sortedSpots.map((spot) => spot.key).filter((key, index, values) => !!key && values.indexOf(key) === index),
        [sortedSpots]
    );

	const spotsInfo = sortedSpots.map((spot: Spot) => ({
        id: spot.id,
        key: spot.key,
        name: spot.name,
        type: spot.type
    } as SpotInfo));

    const selectedSpot = appContext.getSelection();
    const selectSpot = (spot: Spot) => {
        mapContext.setTriggerSelection(undefined);
        mapContext.setSelectedShapeType(undefined);
        appContext.setSelection(spot);
    };

    const {data: spotTranslationsData} = useQuery(getTextTranslationsQuery, {
        variables: {
            areaId: area?.id,
            clientIds: spotClientIds,
            languageCodes: areaLanguageCodes,
            translationKeys: SPOT_TRANSLATION_FIELDS.map((field) => field.translationKey),
            publishedAt: appContext.getDisplayedVersion()?.publishedAt || 0
        },
        skip: !area?.id || areaLanguageCodes.length === 0 || spotClientIds.length === 0,
        fetchPolicy: "network-only"
    });

    useEffect(() => {
        setSpotTranslations(spotTranslationsData?.getTextTranslations || []);
    }, [spotTranslationsData]);

    const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const getDuplicateName = (sourceName: string) => {
        const baseName = sourceName.trim() || "Spot";
        const onlyNumberMatch = /^(\d+)$/.exec(baseName);
        const spacedNumberMatch = /^(.*\S)\s+(\d+)$/.exec(baseName);
        const existingNames = new Set(spots.map((spot) => (spot.name || "").trim()).filter((name) => name.length > 0));

        if (onlyNumberMatch) {
            const numericMatch = /^(\d+)$/.exec(baseName);
            if (numericMatch) {
                const matcher = /^(\d+)$/;
                let maxSuffix = 0;
                existingNames.forEach((name) => {
                    const match = matcher.exec(name);
                    if (!match) {
                        return;
                    }
                    const num = parseInt(match[1], 10);
                    if (Number.isFinite(num)) {
                        maxSuffix = Math.max(maxSuffix, num);
                    }
                });
                if (maxSuffix === 0) {
                    const baseValue = parseInt(baseName, 10);
                    if (Number.isFinite(baseValue)) {
                        maxSuffix = baseValue;
                    }
                }
                return `${maxSuffix + 1}`;
            }
        }

        if (spacedNumberMatch) {
            const prefix = spacedNumberMatch[1].trimEnd();
            const matcher = new RegExp(`^${escapeRegExp(prefix)}\\s+(\\d+)$`);
            let maxSuffix = 0;
            existingNames.forEach((name) => {
                const match = matcher.exec(name);
                if (!match) {
                    return;
                }
                const num = parseInt(match[1], 10);
                if (Number.isFinite(num)) {
                    maxSuffix = Math.max(maxSuffix, num);
                }
            });
            if (maxSuffix === 0) {
                const baseValue = parseInt(spacedNumberMatch[2], 10);
                if (Number.isFinite(baseValue)) {
                    maxSuffix = baseValue;
                }
            }
            return `${prefix} ${maxSuffix + 1}`;
        }

        const matcher = new RegExp(`^${escapeRegExp(baseName)}\\s+(\\d+)$`);
        let maxSuffix = 0;
        existingNames.forEach((name) => {
            const match = matcher.exec(name);
            if (!match) {
                return;
            }
            const num = parseInt(match[1], 10);
            if (Number.isFinite(num)) {
                maxSuffix = Math.max(maxSuffix, num);
            }
        });
        const nextSuffix = Math.max(maxSuffix + 1, 2);
        return `${baseName} ${nextSuffix}`;
    };

    const cloneBeaconTriggers = (triggers?: BeaconTrigger[]) => (
        triggers?.map((trigger) => ({
            ...trigger,
            id: uuidv4()
        }))
    );

    const cloneWiFiTriggers = (triggers?: WiFiApTrigger[]) => (
        triggers?.map((trigger) => ({
            ...trigger,
            id: uuidv4()
        }))
    );

    const cloneGpsTriggers = (triggers?: GpsTrigger[]) => (
        triggers?.map((trigger) => ({
            ...trigger,
            id: uuidv4(),
            polygonCoords: trigger.polygonCoords ? trigger.polygonCoords.map((coord) => ({...coord})) : trigger.polygonCoords,
            circlePosition: trigger.circlePosition ? {...trigger.circlePosition} : trigger.circlePosition
        }))
    );

    const cloneTriggerRules = (rules?: TriggerRules): TriggerRules | undefined => {
        if (!rules) {
            return undefined;
        }
        return {
            logic: rules.logic,
            rules: (rules.rules || []).map((rule: TriggerRule) => ({
                ...rule,
                id: uuidv4()
            }))
        };
    };

    const buildCreateSpot = (source: Spot, name: string): Spot => {
        const baseSpot: Spot = {
            id: "",
            area: area?.id || source.area || "EMPTY-AREA",
            name,
            type: source.type,
            priority: source.priority,
            beaconSensitivity: source.beaconSensitivity,
            pauseOtherAmbients: source.pauseOtherAmbients,
            duckOtherAmbients: source.duckOtherAmbients,
            followMainSound: source.followMainSound,
            playMode: source.playMode,
            playFullAfter: source.playFullAfter,
            loop: source.loop,
            runOnes: source.runOnes,
            modifiedAt: 0,
            key: ""
        };

        if (source.type === SpotType.Marker) {
            return {
                ...baseSpot,
                position: (source as MarkerSpot).position
            } as MarkerSpot;
        }
        if (source.type === SpotType.Circle) {
            return {
                ...baseSpot,
                center: (source as CircleSpot).center,
                radius: (source as CircleSpot).radius
            } as CircleSpot;
        }
        return {
            ...baseSpot,
            coords: (source as PolygonSpot).coords
        } as PolygonSpot;
    };

    const buildDuplicateSpot = (createdSpot: Spot, sourceSpot: Spot, name: string): Spot => {
        let updatedSpot: Spot = {
            ...createdSpot,
            ...sourceSpot,
            id: createdSpot.id,
            key: createdSpot.key,
            modifiedAt: createdSpot.modifiedAt,
            area: createdSpot.area || sourceSpot.area,
            name,
            soundFiles: sourceSpot.soundFiles ? sourceSpot.soundFiles.map((file) => ({...file})) : undefined,
            beaconTriggers: cloneBeaconTriggers(sourceSpot.beaconTriggers),
            wiFiTriggers: cloneWiFiTriggers(sourceSpot.wiFiTriggers),
            gpsTriggers: cloneGpsTriggers(sourceSpot.gpsTriggers),
            triggerRules: cloneTriggerRules(sourceSpot.triggerRules)
        };

        if (sourceSpot.type === SpotType.Marker) {
            updatedSpot = {
                ...updatedSpot,
                position: (sourceSpot as MarkerSpot).position
            } as MarkerSpot;
        } else if (sourceSpot.type === SpotType.Circle) {
            updatedSpot = {
                ...updatedSpot,
                center: (sourceSpot as CircleSpot).center,
                radius: (sourceSpot as CircleSpot).radius
            } as CircleSpot;
        } else {
            updatedSpot = {
                ...updatedSpot,
                coords: (sourceSpot as PolygonSpot).coords
            } as PolygonSpot;
        }

        return updatedSpot;
    };

		const recomputeSplit = () => {
			if (!containerRef.current) return;

			const rect = containerRef.current.getBoundingClientRect();
			const usableHeight = rect.height - dividerHeightPx;
			if (usableHeight <= 0) return;

			const maxBottom = Math.max(minBottomPx, usableHeight - minTopPx);
			const clampedBottom = Math.max(minBottomPx, Math.min(maxBottom, bottomHeightPx));
			setBottomHeightPx(clampedBottom);
		};

	    useLayoutEffect(() => {
	        recomputeSplit();
	        // eslint-disable-next-line react-hooks/exhaustive-deps
	    }, [selectedSpot, sortedSpots.length, bottomHeightPx]);

	    useEffect(() => {
	        const onResize = () => recomputeSplit();
	        window.addEventListener("resize", onResize);
	        return () => window.removeEventListener("resize", onResize);
	        // eslint-disable-next-line react-hooks/exhaustive-deps
	    }, [selectedSpot, sortedSpots.length, bottomHeightPx]);

    useEffect(() => {
        if (selectedSpot) {
            const fallback = "Spot " + ((sortedSpots.findIndex(s => s.id === selectedSpot.id) + 1) || "");
            setNameInput(selectedSpot.name || fallback);
        } else {
            setNameInput("");
        }
        // Only update when selection or its name changes to avoid resetting caret while editing
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSpot?.id, selectedSpot?.name]);

    useEffect(() => {
        if (!selectedSpot) return;
        const node = listItemRefs.current.get(selectedSpot.id);
        node?.scrollIntoView({block: "nearest"});
    }, [selectedSpot, sortedSpots]);

    const buildSpotTranslationFields = (spot: Spot, index: number) => {
        const spotNameDefaultValue = spot.name ? spot.name : "Spot " + (index + 1);
        const nameFallbackValue = selectedSpot?.id === spot.id ? (nameInput || selectedSpot.name || spotNameDefaultValue) : spotNameDefaultValue;
        return SPOT_TRANSLATION_FIELDS.map((field) => {
            if (field.translationKey === "spot.name") {
                return {
                    ...field,
                    fallbackValue: nameFallbackValue
                };
            }
            if (field.translationKey === "spot.summary") {
                return {
                    ...field,
                    fallbackValue: spot.summary
                };
            }
            return {
                ...field,
                fallbackValue: spot.description
            };
        });
    };

    const spotTranslationRowsByClientId = useMemo(
        () => buildSpotTranslationRowsByClientId(spotTranslations),
        [spotTranslations]
    );

    const getSpotTranslationState = (spot: Spot) =>
        computeSpotTranslationState(spot, spotTranslationRowsByClientId, translatableLanguageCodes);

    const getLocalizedStatusColor = (state: LocalizedContentState, isSelected: boolean) => {
        if (isSelected) {
            if (state === "fullyTranslated") {
                return "#15803d";
            }
            if (state === "partiallyTranslated") {
                return "#d97706";
            }
            return "#a0aec0";
        }

        if (state === "fullyTranslated") {
            return "#16a34a";
        }
        if (state === "partiallyTranslated") {
            return "#dd6b20";
        }
        return "#a0aec0";
    };

    const getLocalizedStatusLabel = (state: LocalizedContentState) => {
        if (state === "fullyTranslated") {
            return "Fully translated";
        }
        if (state === "partiallyTranslated") {
            return "Partially translated";
        }
        return "Not translated";
    };

    const getSpotAudioLocalizationState = (spot: Spot) => {
        if (areaLanguages.length <= 1) {
            return undefined;
        }

        const soundFiles = spot.soundFiles || [];
        const applicableSlots = [false, true].filter((isAmbient) => soundFiles.some((soundFile) => (soundFile.ambient ?? false) === isAmbient));

        if (applicableSlots.length === 0) {
            return undefined;
        }

        const localizedEntries = applicableSlots.reduce((count, isAmbient) => {
            const slotLanguageKeys = new Set(
                soundFiles
                    .filter((soundFile) => (soundFile.ambient ?? false) === isAmbient)
                    .map((soundFile) => {
                        const normalizedLanguage = normalizeLanguageCode(soundFile.language);
                        return normalizedLanguage || "__fallback__";
                    })
            );
            return count + Math.max(0, Math.min(slotLanguageKeys.size - 1, areaLanguages.length - 1));
        }, 0);

        const totalRequiredLocalizedEntries = applicableSlots.length * Math.max(areaLanguages.length - 1, 0);
        if (localizedEntries === 0) {
            return "notTranslated" as LocalizedContentState;
        }

        if (localizedEntries >= totalRequiredLocalizedEntries) {
            return "fullyTranslated" as LocalizedContentState;
        }

        return "partiallyTranslated" as LocalizedContentState;
    };

    const mergeSpotTranslation = (savedTranslation: TextTranslation) => {
        setSpotTranslations((previous) => {
            const filtered = previous.filter((entry) => entry.id !== savedTranslation.id &&
                !(entry.clientId === savedTranslation.clientId &&
                    normalizeLanguageCode(entry.languageCode) === normalizeLanguageCode(savedTranslation.languageCode) &&
                    entry.translationKey === savedTranslation.translationKey));
            return [...filtered, savedTranslation];
        });
    };

    useEffect(() => {
        if (!isDragging) return;
            const onMouseMove = (event: MouseEvent) => {
                if (!containerRef.current) return;
                const rect = containerRef.current.getBoundingClientRect();
                const usableHeight = rect.height - dividerHeightPx;
                const offsetY = event.clientY - rect.top;
                const proposedBottom = usableHeight - offsetY;
                const maxBottom = Math.max(minBottomPx, usableHeight - minTopPx);
                const clampedBottom = Math.max(minBottomPx, Math.min(maxBottom, proposedBottom));
                setBottomHeightPx(clampedBottom);
            };
        const onMouseUp = () => {
            setIsDragging(false);
            if (originalUserSelect !== undefined) {
                document.body.style.userSelect = originalUserSelect;
                setOriginalUserSelect(undefined);
            }
        };
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
        };
    }, [isDragging, originalUserSelect]);

    const renderDetails = () => {
        if (!selectedSpot) {
            return <Box padding={4}><Text>Select a spot to view details</Text></Box>;
        }
        const spotNameDefaultValue = selectedSpot.name ? selectedSpot.name : "Spot " + (spots.findIndex(s => s.id === selectedSpot.id) + 1);
        const handleDeleteSpot = async () => {
            const ambientZones = area?.zones?.ambientZones || [];
            const scanResult = findReferencesToSpot(selectedSpot.key, spots, ambientZones);
            const approved = await confirm({
                title: "Delete spot",
                body: buildDeleteSpotConfirmBody(scanResult),
                confirmText: "Delete",
                isDestructive: true
            });
            if (approved) {
                appContext.deleteSelectedSpot(selectedSpot);
            }
        };
        const handleDuplicateSpot = () => {
            if (!appContext.isDraft() || !selectedSpot) {
                return;
            }
            const nextName = getDuplicateName(selectedSpot.name || spotNameDefaultValue);
            const createSpot = buildCreateSpot(selectedSpot, nextName);
            const createPromise = appContext.createSpot(createSpot, {select: false});
            if (!createPromise) {
                return;
            }
            createPromise.then((createdSpot) => {
                if (!createdSpot) {
                    return;
                }
                const duplicatedSpot = buildDuplicateSpot(createdSpot, selectedSpot, nextName);
                const updatePromise = appContext.updateSpot(createdSpot, duplicatedSpot);
                if (updatePromise) {
                    updatePromise.then((updatedSpot) => {
                        appContext.setSelection(updatedSpot || createdSpot);
                    });
                } else {
                    appContext.setSelection(createdSpot);
                }
            });
        };

        return <Box padding={"8px"} height={"100%"} overflowY={"auto"} key={selectedSpot.id} data-testid={"spot-details"}
                    data-spot-id={selectedSpot.id}>
            <Grid gridTemplateColumns={"1fr min-content"} gap={2} alignItems={"center"} marginBottom={2}>
                <Input type={"text"} value={nameInput}
                       data-testid={"spot-name-input"}
                       disabled={!appContext.isDraft()}
                       onChange={(e) => setNameInput(e.target.value)}
                       onKeyDown={(e) => {
                           if (e.key === "Enter" && selectedSpot) {
                               appContext.updateSpot(selectedSpot, {
                                   ...selectedSpot,
                                   name: nameInput || spotNameDefaultValue
                               });
                           }
                       }}
                       onBlur={() =>
                           selectedSpot && appContext.updateSpot(selectedSpot, {
                               ...selectedSpot,
                               name: nameInput || spotNameDefaultValue
                           })}/>
                {appContext.isDraft() ? (
                    <HStack spacing={1}>
                        <Tooltip label={"Duplicate spot"}>
                            <IconButton
                                variant={"ghost"}
                                aria-label={"Duplicate"}
                                icon={<BiCopy/>}
                                onClick={handleDuplicateSpot}
                                disabled={!appContext.isDraft()}
                            />
                        </Tooltip>
                        <Tooltip label={"Delete spot"}>
                            <IconButton
                                variant={"ghost"}
                                aria-label={"Delete"}
                                icon={<BiTrash/>}
                                onClick={handleDeleteSpot}
                                disabled={!appContext.isDraft()}
                            />
                        </Tooltip>
                    </HStack>
                ) : null}
            </Grid>
                <SpotDetailsAccordion
                    spot={selectedSpot}
                    spotsInfo={spotsInfo}
                    openSections={spotAccordionSections}
                    onOpenSectionsChange={setSpotAccordionSections}
                />

        </Box>;
    }

    const rowsTemplate = `1fr ${dividerHeightPx}px ${bottomHeightPx}px`;

    return <Container maxWidth={"full"} maxHeight={"full"} height={"100%"} paddingTop={"8px"} paddingBottom={"6px"} overflow={"auto"}>
				<Grid height={"100%"} gridTemplateRows={rowsTemplate} flex={"1"} ref={containerRef} minHeight={0}>
            <Box overflowY={"auto"} ref={listScrollRef} minHeight={0} data-testid={"spot-list"}>
                {sortedSpots.map((spot: Spot, index: number) => {
                    if (!spot) return null;
					const isSelected = selectedSpot?.id === spot.id || mapContext.getTriggerSelection() === spot.id;
					const spotNameDefaultValue = spot.name ? spot.name : "Spot fake " + (index + 1);
                    const hasSound = !!spot.soundFiles && spot.soundFiles.length > 0;
                    const hasBeacon = !!spot.beaconTriggers && spot.beaconTriggers.some(tr => isValidBeaconAddress(tr.macAddr));
                    const hasWifi = !!spot.wiFiTriggers && spot.wiFiTriggers.some(tr => !!tr.name && tr.name.trim().length > 0);
                    const translationState = getSpotTranslationState(spot);
                    const audioLocalizationState = getSpotAudioLocalizationState(spot);
                    return <Box key={spot.id}
                                data-testid={`spot-row-${spot.id}`}
                                data-selected={isSelected ? "true" : "false"}
                                paddingY={"6px"}
                                paddingX={"4px"}
                                cursor={"pointer"}
                                ref={(el) => {
                                    if (el) {
                                        listItemRefs.current.set(spot.id, el);
                                    } else {
                                        listItemRefs.current.delete(spot.id);
                                    }
                                }}
                                onMouseDown={(event) => {
                                    if (event.button !== 0) {
                                        return;
                                    }
                                    if (document.activeElement instanceof HTMLElement) {
                                        document.activeElement.blur();
                                    }
                                    selectSpot(spot);
                                }}>
                        <Grid templateColumns={"max-content 1fr max-content"} alignItems={"center"} gap={2}>
                            <Box color={isSelected ? "#d97706" : undefined} fontWeight={isSelected ? "semibold" : "normal"}>
                                {spotNameDefaultValue}
                            </Box>
                            <Box flexGrow={1} borderBottom={"1px solid #e2e8f0"}/>
                            <Grid templateColumns={translatableLanguageCodes.length > 0 ? `repeat(4, ${ICON_SLOT_SIZE_PX}px)` : `repeat(3, ${ICON_SLOT_SIZE_PX}px)`} gap={1} alignItems={"center"} justifyItems={"center"}>
                                {translatableLanguageCodes.length > 0 ? (
                                    <Box width={`${ICON_SLOT_SIZE_PX}px`} height={`${ICON_SLOT_SIZE_PX}px`} display={"flex"} alignItems={"center"} justifyContent={"center"}>
                                        <Tooltip label={getLocalizedStatusLabel(translationState || "notTranslated")}>
                                            <IconButton
                                                aria-label={"Spot translations"}
                                                icon={<FiFlag color={getLocalizedStatusColor((translationState || "notTranslated"), isSelected)}/>}
                                                variant={"ghost"}
                                                size={"xs"}
                                                minWidth={`${ICON_SLOT_SIZE_PX}px`}
                                                height={`${ICON_SLOT_SIZE_PX}px`}
                                                opacity={1}
                                                onMouseDown={(event) => {
                                                    event.stopPropagation();
                                                }}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    selectSpot(spot);
                                                    openTranslationDialog({
                                                        areaId: area?.id,
                                                        clientId: spot.key,
                                                        defaultLanguage: area?.languages?.default,
                                                        availableLanguages: areaLanguages,
                                                        publishedAt: appContext.getDisplayedVersion()?.publishedAt || 0,
                                                        canEdit: appContext.isDraft(),
                                                        title: `Translations: ${spot.name || "Spot"}`,
                                                        description: "Translate spot name, summary, and description. If a translation is left empty, it falls back to the space default language and then the main spot field value.",
                                                        fields: buildSpotTranslationFields(spot, sortedSpots.findIndex((s) => s.id === spot.id)),
                                                        onTranslationSaved: mergeSpotTranslation
                                                    });
                                                }}
                                            />
                                        </Tooltip>
                                    </Box>
                                ) : null}
                                <Box width={`${ICON_SLOT_SIZE_PX}px`} height={`${ICON_SLOT_SIZE_PX}px`} display={"flex"} alignItems={"center"} justifyContent={"center"}>
                                    {hasSound ? (
                                        audioLocalizationState ? (
                                            <Tooltip label={`Localized audio: ${getLocalizedStatusLabel(audioLocalizationState).toLowerCase()}`}>
                                                <Box display={"inline-flex"} alignItems={"center"}>
                                                    <BsSoundwave color={getLocalizedStatusColor(audioLocalizationState, isSelected)}/>
                                                </Box>
                                            </Tooltip>
                                        ) : (
                                            <BsSoundwave color={isSelected ? "#d97706" : undefined}/>
                                        )
                                    ) : null}
                                </Box>
                                <Box width={`${ICON_SLOT_SIZE_PX}px`} height={`${ICON_SLOT_SIZE_PX}px`} display={"flex"} alignItems={"center"} justifyContent={"center"}>
                                    {hasBeacon ? <FaBluetoothB color={isSelected ? "#d97706" : undefined}/> : null}
                                </Box>
                                <Box width={`${ICON_SLOT_SIZE_PX}px`} height={`${ICON_SLOT_SIZE_PX}px`} display={"flex"} alignItems={"center"} justifyContent={"center"}>
                                    {hasWifi ? <FaWifi color={isSelected ? "#d97706" : undefined}/> : null}
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                })}
            </Box>
            <Box height={"6px"} backgroundColor={"#e2e8f0"} cursor={"row-resize"} onMouseDown={() => {
                setIsDragging(true);
                if (originalUserSelect === undefined) {
                    setOriginalUserSelect(document.body.style.userSelect);
                }
                document.body.style.userSelect = "none";
            }} />
            <Box overflowY={"auto"} minHeight={0}>
                {renderDetails()}
            </Box>
		</Grid>
	</Container>;
}

export default SpotList;
